import React from 'react';
import { Upload, Type, FileText, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const DOC_TYPES = ['Quote', 'Invoice', 'Order Confirmation', 'Packing Slip', 'Other'];

/**
 * Dialog for adding a new document via file upload OR pasted text. Fully
 * controlled — parent owns all form state and the save handler.
 */
const AddDocumentDialog = ({
  open,
  onOpenChange,
  uploadTab,
  setUploadTab,
  selectedFile,
  setSelectedFile,
  newDoc,
  setNewDoc,
  projects,
  orders,
  creating,
  onSave,
  onProjectChange,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border border-zinc-200 rounded-none p-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-0 flex-shrink-0">
          <DialogTitle className="text-2xl font-heading font-bold uppercase tracking-wide">Add Document</DialogTitle>
        </DialogHeader>

        <div className="p-6 pt-4 overflow-y-auto flex-1 space-y-4">
          <Tabs value={uploadTab} onValueChange={setUploadTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 border border-zinc-200 rounded-none h-12">
              <TabsTrigger value="file" data-testid="tab-file-upload" className="rounded-none font-bold uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-zinc-900">
                <Upload className="w-4 h-4 mr-2" /> Upload File
              </TabsTrigger>
              <TabsTrigger value="text" data-testid="tab-paste-text" className="rounded-none font-bold uppercase text-xs data-[state=active]:bg-primary data-[state=active]:text-zinc-900">
                <Type className="w-4 h-4 mr-2" /> Paste Text
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div
                className={`border-2 border-dashed border-black p-6 text-center cursor-pointer hover:bg-zinc-50 transition-colors ${
                  selectedFile ? 'bg-green-50 border-green-500' : ''
                }`}
                onClick={() => document.getElementById('file-input').click()}
                data-testid="file-drop-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setSelectedFile(file);
                      if (!newDoc.title) {
                        setNewDoc(prev => ({ ...prev, title: file.name.replace(/\.[^.]+$/, '') }));
                      }
                    }
                  }}
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-bold text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-zinc-500">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} className="p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto mb-2 text-zinc-500" />
                    <p className="font-bold text-sm">Click to upload PDF or image</p>
                    <p className="text-xs text-zinc-500 mt-1">PDF, PNG, JPG, WebP</p>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="text" className="mt-4">
              <textarea
                placeholder="Paste email or document text here..."
                value={newDoc.text_content}
                onChange={(e) => setNewDoc({ ...newDoc, text_content: e.target.value })}
                data-testid="text-content-input"
                className="w-full h-32 border border-zinc-200 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </TabsContent>
          </Tabs>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Title / Subject *</label>
              <input
                type="text"
                value={newDoc.title}
                onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                placeholder="e.g., Ferguson Quote #4821"
                data-testid="doc-title-input"
                className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Sender</label>
              <input
                type="text"
                value={newDoc.sender}
                onChange={(e) => setNewDoc({ ...newDoc, sender: e.target.value })}
                placeholder="e.g., Ferguson Sales"
                data-testid="doc-sender-input"
                className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Date Received</label>
              <input
                type="date"
                value={newDoc.date_received}
                onChange={(e) => setNewDoc({ ...newDoc, date_received: e.target.value })}
                data-testid="doc-date-input"
                className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Document Type</label>
            <select
              value={newDoc.document_type}
              onChange={(e) => setNewDoc({ ...newDoc, document_type: e.target.value })}
              data-testid="doc-type-select"
              className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Assign to Project (optional)</label>
            <select
              value={newDoc.project_id}
              onChange={(e) => onProjectChange(e.target.value)}
              data-testid="doc-project-select"
              className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- No Project --</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
          </div>

          {newDoc.project_id && orders.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Link to Order (optional)</label>
              <select
                value={newDoc.order_id}
                onChange={(e) => setNewDoc({ ...newDoc, order_id: e.target.value })}
                data-testid="doc-order-select"
                className="w-full h-12 border border-zinc-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- No Order --</option>
                {orders.map(o => (
                  <option key={o.order_id} value={o.order_id}>
                    {o.vendor_name}{o.po_number ? ` - ${o.po_number}` : ''} ({o.status})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="p-6 pt-0 border-t-2 border-black bg-white flex-shrink-0">
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 h-14 bg-white text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={creating}
              data-testid="save-document-btn"
              className="flex-1 h-14 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-primary-hover active:translate-y-1 disabled:opacity-50"
            >
              {creating ? 'Saving...' : 'Save Document'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddDocumentDialog;
